import React from 'react';
import { ThemeOptions } from '@mui/material/styles';
import { createTheme } from '@mui/material';
// https://bareynol.github.io/mui-theme-creator/

declare module '@mui/material/styles' {
    interface Backgrounds {
        bg1: string;
        bg2: string;
        bg3: string;
        bg4: string;
        bg5: string;
        bg6: string;
        sideBarBackground: string;
        pageHeaderBackground: string;
        layoutHeaderBg: string;
        progressDefaultBg: string;
        loginFormBg: string;
        loginInputsBg: string;
        // ---dashboard specific---
        tableHeadBg?: string;
        tableRowBg?: string;
        tableFooterBg?: string;
        successDark100?: string;
        errorDark100?: string;
        errorDark50?: string;
        modalBlurredBg?: string;
        modalBodyBg?: string;
        //------HL Options--------
        bg7?: string;
        bg8?: string;
        bg0?: string;
        buttonSelectBg?: string;
        grayButtonBg?: string;
        turquoiseBg?: string;
        greenLightningBg?: string;
        bullMeterVeryBullishBg?: string;
        bullMeterBullishBg?: string;
        bullMeterSlightlyBullishBg?: string;
        bullMeterVeryBearishBg?: string;
        bullMeterBearishBg?: string;
        bullMeterSlightlyBearishBg?: string;
        htBgGradient?: string;
        heatmapTableBg?: string;
    }

    interface CustomTextColor {
        text1: string;
        text2: string;
        text3: string;
        text4: string;
        text5: string;
        text6: string;
        text7: string;
        text8?: string;
        text9?: string;
        drawerTextColor: string;
        textDark?: string;
    }

    interface CustomColors {
        halfTransparentHL: string;
        bronze: string;
    }

    interface HypertrackerPaletteColor {
        main: string;
        light?: string;
        dark?: string;
    }

    interface Palette {
        backgrounds: Backgrounds;
        customText: CustomTextColor;
        customColors?: CustomColors;
        redHalfTransparent?: string;
        greenHalfTransparent?: string;
        blueHalfTransparent?: string;
        yellowHalfTransparent?: string;
        whiteHalfTransparent?: string;
        whiteTransparent?: string;
        hyperLiquidColor: string;
        hyperLiquidColor2: string;
        hypertracker: HypertrackerPaletteColor;
    }

    // allow configuration using `createTheme`
    interface PaletteOptions {
        backgrounds: Backgrounds;
        customText: CustomTextColor;
        customColors?: CustomColors;
        redHalfTransparent?: string;
        greenHalfTransparent?: string;
        blueHalfTransparent?: string;
        yellowHalfTransparent?: string;
        whiteHalfTransparent?: string;
        whiteTransparent?: string;
        hyperLiquidColor: string;
        hyperLiquidColor2: string;
        hypertracker: HypertrackerPaletteColor;
    }

    interface TypographyVariants {
        p20: React.CSSProperties;
        p18: React.CSSProperties;
        p16: React.CSSProperties;
        p14: React.CSSProperties;
        p12: React.CSSProperties;
        p10: React.CSSProperties;
        p22: React.CSSProperties;
    }

    // allow configuration using `createTheme`
    interface TypographyVariantsOptions {
        p20: React.CSSProperties;
        p18: React.CSSProperties;
        p16: React.CSSProperties;
        p14: React.CSSProperties;
        p12: React.CSSProperties;
        p10: React.CSSProperties;
        p22: React.CSSProperties;
    }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        p20: true;
        p18: true;
        p16: true;
        p14: true;
        p12: true;
        p10: true;
        p22: true;
    }
}

declare module '@mui/material/Button' {
    interface ButtonPropsColorOverrides {
        hypertracker: true;
    }
}

export const lightThemeOptions: ThemeOptions = {
    palette: {
        mode: 'light',
        primary: {
            main: '#247FFF',
            light: '#579DFF',
            dark: '#0B4EAC',
        },
        secondary: {
            main: '#FFAB00',
            dark: '#db8a11',
        },
        background: {
            default: '#FAFBFF',
        },
        text: {
            primary: '#43485C',
            secondary: '#727687',
            // hint: '#B0B7C3',
        },
        error: {
            main: '#FD6E4E',
            dark: '#4B2016',
        },
        warning: {
            main: '#FFAB00',
            light: '#FFAB001A',
        },
        info: {
            main: '#43485C',
        },
        success: {
            main: '#38CB89',
            dark: '#255744',
        },
        hypertracker: {
            main: '#15C8B1',
            light: '#8CF7E0',
            dark: '#12AA96',
        },
        divider: '#EDF0F7',
        hyperLiquidColor: '#15C8B1',
        hyperLiquidColor2: '#8CF7E0',
        backgrounds: {
            bg1: '#ffffff',
            bg2: '#EDF0F7',
            bg3: '#ffffff',
            bg4: '#ffffff',
            bg5: '#262731',
            bg6: '#F5F7FB',
            sideBarBackground: '#0B4EAC',
            layoutHeaderBg: '#FFFFFF',
            pageHeaderBackground: '#ffffff',
            progressDefaultBg: '#E7E8EB',
            loginFormBg: '#26272C',
            loginInputsBg: '#3B3C46',
            tableHeadBg: '#F0F6FF',
            tableRowBg: '#FAFBFF',
            tableFooterBg: '#EEF1F5',
            successDark100: '#2F8C64',
            errorDark100: '#743D33',
            errorDark50: '#BE5841',
            modalBodyBg: '#FFFFFF',
            modalBlurredBg: '#1A1B234D',
        },
        customText: {
            text1: '#707077',
            text2: '#43485C',
            text3: '#B0B7C3',
            text4: '#FFF',
            text5: '#1a1b23',
            text6: '#001C42',
            text7: '#000000',
            drawerTextColor: '#FFFFFF',
            textDark: '#001C42',
        },
    },
    typography: {
        fontFamily: ['Manrope', 'Serif'].join(','),
        p20: {
            fontSize: '20px',
            lineHeight: '30px',
            fontWeight: '500',
        },
        p18: {
            fontSize: '18px',
            lineHeight: '27px',
            fontWeight: '500',
        },
        p16: {
            fontSize: '16px',
            lineHeight: '24px',
            fontWeight: '500',
        },
        p14: {
            fontSize: '14px',
            lineHeight: '21px',
            fontWeight: '500',
        },
        p12: {
            fontSize: '12px',
            lineHeight: '17px',
            fontWeight: '500',
        },
        p10: {
            fontSize: '10px',
            lineHeight: '15px',
            fontWeight: '500',
        },
        p22: {
            fontSize: '22px',
            lineHeight: '33px',
            fontWeight: '500',
        },
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 768,
            md: 900,
            lg: 1200,
            xl: 1536,
        },
    },
    components: {
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    textTransform: 'unset',
                    fontWeight: '500',
                    borderRadius: '6px',
                    '&:hover': {
                        borderRadius: '6px',
                    },
                },
            },
        },
    },
};

export const darkThemeOptions: ThemeOptions = {
    palette: {
        mode: 'dark',
        primary: {
            main: '#247FFF',
            light: '#579DFF',
            dark: '#0B4EAC',
        },
        secondary: {
            main: '#FFAB00',
            dark: '#db8a11',
        },
        background: {
            default: '#1A1B23',
        },
        text: {
            primary: '#fff',
            secondary: '#727687',
            // hint: '#B0B7C3',
        },
        error: {
            main: '#FD6E4E',
            light: '#ff7575',
            dark: '#4B2016',
        },
        warning: {
            main: '#FFAB00',
            light: '#FFAB001A',
        },
        info: {
            main: '#FFF',
        },
        success: {
            main: '#38CB89',
            light: '#77d0aa',
            dark: '#255744',
        },
        hypertracker: {
            main: '#15C8B1',
            light: '#8CF7E0',
            dark: '#12AA96',
        },
        divider: '#3B3C46',
        hyperLiquidColor: '#15C8B1',
        hyperLiquidColor2: '#8CF7E0',
        backgrounds: {
            bg1: '#1A1B23',
            bg2: '#1D1E25',
            bg3: '#26272C',
            bg4: '#3B3C46',
            bg5: '#262731',
            bg6: '#F5F7FB',
            sideBarBackground: '#0E0E0E',
            layoutHeaderBg: '#1A1B23',
            pageHeaderBackground: '#1A1B23',
            progressDefaultBg: '#3B3C46',
            loginFormBg: '#26272C',
            loginInputsBg: '#3B3C46',
            tableHeadBg: '#26272C',
            tableRowBg: '#3B3C46',
            tableFooterBg: '#3B3C46',
            successDark100: '#2F8C64',
            errorDark100: '#743D33',
            errorDark50: '#BE5841',
            modalBodyBg: '#3B3C46',
            modalBlurredBg: '#1A1B230A',
        },
        customText: {
            text1: '#707077',
            text2: '#F0F0F0',
            text3: '#B0B7C3',
            text4: '#FFF',
            text5: '#1a1b23',
            text6: '#FFF',
            text7: '#000000',
            drawerTextColor: '#777E90',
            textDark: '#001C42',
        },
    },
    typography: {
        fontFamily: ['Manrope', 'Serif'].join(','),
        p20: {
            fontSize: '20px',
            lineHeight: '30px',
            fontWeight: '500',
        },
        p18: {
            fontSize: '18px',
            lineHeight: '27px',
            fontWeight: '500',
        },
        p16: {
            fontSize: '16px',
            lineHeight: '24px',
            fontWeight: '500',
        },
        p14: {
            fontSize: '14px',
            lineHeight: '21px',
            fontWeight: '500',
        },
        p12: {
            fontSize: '12px',
            lineHeight: '17px',
            fontWeight: '500',
        },
        p10: {
            fontSize: '10px',
            lineHeight: '15px',
            fontWeight: '500',
        },
        p22: {
            fontSize: '22px',
            lineHeight: '33px',
            fontWeight: '500',
        },
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 768,
            md: 900,
            lg: 1200,
            xl: 1536,
        },
    },
    components: {
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    textTransform: 'unset',
                    fontWeight: '500',
                    borderRadius: '6px',
                    '&:hover': {
                        borderRadius: '6px',
                    },
                },
            },
        },
    },
};

export const hlDarkThemeOptions: ThemeOptions = {
    palette: {
        mode: 'dark',
        primary: {
            main: '#247FFF',
            light: '#579DFF',
            dark: '#0B4EAC',
        },
        secondary: {
            main: '#FFAB00',
            dark: '#db8a11',
        },
        background: {
            default: '#1A1B23',
        },
        text: {
            primary: '#fff',
            secondary: '#727687',
            // hint: '#B0B7C3',
        },
        error: {
            main: '#FD6E4E',
            light: '#ff7575',
        },
        warning: {
            main: '#FFAB00',
        },
        info: {
            main: '#FFF',
        },
        success: {
            main: '#38CB89',
            light: '#77d0aa',
        },
        hypertracker: {
            main: '#15C8B1',
            light: '#8CF7E0',
            dark: '#12AA96',
        },
        divider: 'rgba(255, 255, 255, 0.10)',
        redHalfTransparent: 'rgba(253, 110, 78, 0.2)',
        greenHalfTransparent: 'rgba(56, 203, 137, 0.3)',
        blueHalfTransparent: 'rgba(55, 125, 255, 0.3)',
        yellowHalfTransparent: 'rgba(255, 171, 0, 0.3)',
        whiteHalfTransparent: 'rgba(255, 255, 255, 0.2)',
        whiteTransparent: 'rgba(255, 255, 255, 0.10)',
        hyperLiquidColor: '#15C8B1',
        hyperLiquidColor2: '#8CF7E0',
        backgrounds: {
            bg0: '#000000',
            bg1: '#1A1B23',
            bg2: '#1D1E25',
            bg3: '#26272C',
            bg4: '#3B3C46',
            bg5: '#262731',
            bg6: '#F5F7FB',
            bg7: '#212121',
            bg8: '#14141B',
            buttonSelectBg: 'rgba(140, 247, 224, 0.19)',
            sideBarBackground: '#0E0E0E',
            layoutHeaderBg: '#111117',
            pageHeaderBackground: '#1A1B23',
            progressDefaultBg: '#3B3C46',
            loginFormBg: '#26272C',
            loginInputsBg: '#3B3C46',
            grayButtonBg: '#43485C',
            turquoiseBg: '#8CF7E0',
            greenLightningBg: '#15C8B1',
            bullMeterVeryBullishBg: '#1F3D37',
            bullMeterBullishBg: '#2F5950',
            bullMeterSlightlyBullishBg: '#386C62',
            bullMeterVeryBearishBg: '#5E3430',
            bullMeterBearishBg: '#7A423D',
            bullMeterSlightlyBearishBg: '#884D47',
            htBgGradient: 'linear-gradient(180deg, #0C0C10 0%, #1A1B23 100%)',
            heatmapTableBg: '#0C0D10',
        },

        customColors: {
            halfTransparentHL: 'rgba(140, 247, 224, 0.5)',
            bronze: '#CD5C00',
        },

        customText: {
            text1: '#707077',
            text2: '#43485C',
            text3: '#B0B7C3',
            text4: '#FFF',
            text5: '#1a1b23',
            text6: '#001C42',
            text7: '#A0A2A4',
            text8: '#505762',
            text9: '#F0F0F0',
            drawerTextColor: '#777E90',
        },
    },
    typography: {
        fontFamily: ['IBM Plex Mono', 'monospace'].join(','),
        p20: {
            fontSize: '20px',
            lineHeight: '30px',
            fontWeight: '500',
        },
        p18: {
            fontSize: '18px',
            lineHeight: '27px',
            fontWeight: '500',
        },
        p16: {
            fontSize: '16px',
            lineHeight: '24px',
            fontWeight: '500',
        },
        p14: {
            fontSize: '14px',
            lineHeight: '21px',
            fontWeight: '500',
        },
        p12: {
            fontSize: '12px',
            lineHeight: '17px',
            fontWeight: '500',
        },
        p10: {
            fontSize: '10px',
            lineHeight: '15px',
            fontWeight: '500',
        },
        p22: {
            fontSize: '22px',
            lineHeight: '33px',
            fontWeight: '500',
        },
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 768,
            md: 900,
            lg: 1200,
            xl: 1536,
        },
    },
    components: {
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: ({ ownerState, theme }) => ({
                    ...(ownerState.color === 'hypertracker' && {
                        color: '#000000',
                    }),
                    textTransform: 'unset',
                    fontWeight: '500',
                    borderRadius: '6px',
                    '&:hover': {
                        borderRadius: '6px',
                    },
                }),
            },
        },
    },
};

const defaultLight = createTheme();
export const lightTheme = createTheme({ ...defaultLight, ...lightThemeOptions });
export const darkTheme = createTheme({ ...defaultLight, ...darkThemeOptions });
export const hypertrackerTheme = createTheme({ ...defaultLight, ...hlDarkThemeOptions });
